// TaskCreatePage — форма создания задачи. После успешного create → редирект на /tasks/{id}.
// R38b / U-9: prefill связи со сделкой из query-параметра ?dealId= (создание из карточки сделки):
// dealId уходит в payload POST /api/tasks, визуально как «скрытое» поле (в форме не отображается).
// B1: departmentId — обязательный Select формы (backend @NotNull); fake-UUID убран.
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Alert, Stack } from '@mui/material';
import { PageHeader } from '@/theme/PageHeader';
import { TaskFormFields, type TaskFormValues } from './components/TaskFormFields';
import { tasksApi } from '@/api/tasks';
import { useAuth } from '@/auth/AuthContext';

export function TaskCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  // R38b / U-9: prefill dealId (переходит из таба «Задачи» карточки сделки).
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get('dealId')?.trim() ?? '';

  // U-24: ref-флаг закрывает окно «двойной клик/Enter до ре-рендера isPending=true» —
  // useMutation отдаёт snapshot (useSyncExternalStore), в замыкании isPending не виден
  // до ререндера; кнопка disabled — вторая линия защиты.
  const submittingRef = useRef(false);
  const mutation = useMutation({
    mutationFn: (values: TaskFormValues) =>
      tasksApi.create({
        title: values.title,
        description: values.description || null,
        priority: values.priority,
        assigneeUserId: values.assigneeUserId || null,
        dueAt: values.dueAt || null,
        // B1: департамент — явное поле формы (обязательное), не fake-UUID.
        departmentId: values.departmentId,
        ...(dealId !== '' ? { dealId } : {}),
      }),
    onSuccess: (task) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      navigate(`/tasks/${task.id}`);
    },
    onSettled: () => {
      // U-24: после завершения мутации ref сбрасывается — форма снова submit-доступна.
      submittingRef.current = false;
    },
  });

  return (
    <Box>
      <PageHeader
        title="Новая задача"
        subtitle="Заполните обязательные поля и сохраните"
        actions={null}
      />
      <Stack spacing={2}>
        {mutation.isError ? (
          <Alert severity="error">
            {mutation.error instanceof Error ? mutation.error.message : 'Не удалось создать задачу'}
          </Alert>
        ) : null}
        <TaskFormFields
          // B1: дефолт — первый департамент текущего пользователя; иначе пусто + validation error.
          defaultValues={{ departmentId: currentUser?.departments[0]?.id ?? '' }}
          // U-24: guard — защита окна «двойной клик/Enter до ре-рендера isPending=true».
          onSubmit={(values) => {
            if (submittingRef.current || mutation.isPending) return;
            submittingRef.current = true;
            mutation.mutate(values);
          }}
          submitting={mutation.isPending}
          submitLabel="Создать задачу"
        />
      </Stack>
    </Box>
  );
}
